/**
 * Omar Al-Rashidi (Metabolic Syndrome Pathway Metabolic Syndrome) - PROD-579 fixture.
 *
 * Source-of-truth: HTML-PROTOTYPE-PARITY-AUDIT §A.11 (48M, MetSyn 4 of 5
 * IDF criteria, default chart on first load) + master Pre-flight Eval
 * Suite Category C scenario "Discordant CAC + ApoB (zero CAC, elevated
 * ApoB)" + ORIGINAL-PROTOTYPE-PROMPTS Prompt 8 (Metabolic Syndrome Pathway = Metabolic
 * Syndrome). Verbatim NBA copy from HTML-BEHAVIORAL-EXTRACTION §3 Metabolic Syndrome Pathway
 * (em dashes rewritten per HC5).
 *
 * Tenant: apex-cardiometabolic. Future PROD-568 wires tenant filtering.
 *
 * Per HC1 + ADR-PROTO-002 mitigation 4: every value is synthetic.
 * Per HC5: zero em dashes.
 */
import type { PatientFixture } from './types';

export const omarAlRashidi: PatientFixture = {
  id: 'omar-al-rashidi',
  name: 'Omar Al-Rashidi',
  age: 48,
  sex: 'M',
  tenant: 'apex-cardiometabolic',
  pathway: 'metabolic-syndrome-pathway',
  presetClinicalQuestion:
    'MetSyn 4 of 5 IDF criteria with discordant CAC and ApoB. Confirm Metabolic Syndrome Pathway enrollment and risk stratification.',
  summary:
    'MetSyn 4 of 5 IDF criteria. CAC zero, ApoB elevated. Metabolic Syndrome Pathway Step 1 risk stratification.',
  chart: {
    problemList: [
      'Metabolic syndrome (4 of 5 IDF criteria)',
      'Type 2 diabetes mellitus, well controlled',
      'Mixed hyperlipidemia',
      'Essential hypertension',
      'Non-alcoholic fatty liver disease (NAFLD)',
    ],
    medications: [
      'Metformin 1000 mg PO twice daily',
      'Rosuvastatin 10 mg PO nightly',
      'Amlodipine 5 mg PO daily',
      'Aspirin 81 mg PO daily',
    ],
    vitals: {
      bmi: 33.1,
      heightCm: 175,
      weightKg: 101.4,
      bloodPressure: '142/90 mmHg',
      heartRate: 82,
      temperatureC: 36.6,
    },
    lastVisit: {
      date: '2026-03-15',
      summary:
        'Routine T2DM follow-up. HbA1c 7.4. CAC score zero. ApoB elevated despite statin. Discussed Framingham 16% and Metabolic Syndrome Pathway enrollment.',
    },
  },
  labs: {
    HbA1c: {
      value: 7.4,
      unit: '%',
      referenceRange: '4.0 to 5.6',
      collectedAt: '2026-03-15',
    },
    LDL: {
      value: 88,
      unit: 'mg/dL',
      referenceRange: 'less than 70 (high-risk)',
      collectedAt: '2026-03-15',
    },
    // Discordant: ApoB elevated despite LDL near target.
    ApoB: {
      value: 112,
      unit: 'mg/dL',
      referenceRange: 'less than 80 (high-risk)',
      collectedAt: '2026-03-15',
    },
    eGFR: {
      value: 78,
      unit: 'mL/min/1.73m2',
      referenceRange: 'greater than 60',
      collectedAt: '2026-03-15',
    },
    hsCRP: {
      value: 4.2,
      unit: 'mg/L',
      referenceRange: 'less than 1.0',
      collectedAt: '2026-03-15',
    },
    triglycerides: {
      value: 218,
      unit: 'mg/dL',
      referenceRange: 'less than 150',
      collectedAt: '2026-03-15',
    },
    HDL: {
      value: 36,
      unit: 'mg/dL',
      referenceRange: 'greater than 40',
      collectedAt: '2026-03-15',
    },
    fastingGlucose: {
      value: 134,
      unit: 'mg/dL',
      referenceRange: '70 to 99',
      collectedAt: '2026-03-15',
    },
    fastingInsulin: {
      value: 22,
      unit: 'uIU/mL',
      referenceRange: '2.6 to 24.9',
      collectedAt: '2026-03-15',
    },
    Lp_a: {
      value: 38,
      unit: 'mg/dL',
      referenceRange: 'less than 30',
      collectedAt: '2026-03-15',
    },
  },
  // ICD-10: T2DM no complication, MetSyn (extended group), HTN,
  // mixed hyperlipidemia, NAFLD.
  icd10: ['E11.9', 'E88.81', 'I10', 'E78.5', 'K76.0'],
  nbas: [
    {
      id: 'omar-tlp3-nba-1',
      title: 'Order fasting metabolic panel',
      rationale:
        'FPG, Insulin, Lipid panel, hsCRP, Uric acid, eGFR. Required for Metabolic Syndrome Pathway risk stratification gate.',
      confidence: 0.95,
      riskTier: 'manual_approve',
      actionType: 'order_lab',
      ctaVerb: 'Order',
      cited_sources: [
        {
          id: 'IDF-MetSyn-2006',
          title: 'IDF Consensus Worldwide Definition of the Metabolic Syndrome',
          year: 2006,
        },
        {
          id: 'AACE-2017-Lipid',
          title: 'AACE 2017 Lipid Management Guidelines',
          year: 2017,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Accepted 94% of the time for MetSyn enrollment',
    },
    {
      id: 'omar-tlp3-nba-2',
      title: 'Dispatch DQOLY and AUDIT-C PROMs',
      rationale:
        'Diabetes QoL plus alcohol use screen required at Metabolic Syndrome Pathway enrollment. Sent via JITAI trigger to patient app.',
      confidence: 0.91,
      riskTier: 'review_window',
      actionType: 'dispatch_PROM',
      ctaVerb: 'Dispatch',
      cited_sources: [
        {
          id: 'Scrips-PROM-Engine',
          title: 'Scrips PROM Engine instrument catalog (Metabolic Syndrome Pathway)',
          year: 2026,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Always dispatched at Metabolic Syndrome Pathway Step 1',
    },
    {
      id: 'omar-tlp3-nba-3',
      title: 'Present CVD risk trajectory',
      rationale:
        'Show 10-year CVD risk before and after Metabolic Syndrome Pathway. Framingham 16% to projected 9% at 24 weeks. Increases commitment 2.8x.',
      confidence: 0.73,
      riskTier: 'manual_approve',
      // [IMPLEMENTED][PROD-583] Temporary classification: PathwayView is a
      // UX gesture, not yet registered in ADR-001 Layer 2 closed enum.
      // Classified as `send_educational_message` until Phase 2 ADR-001
      // amendment lands (see PROD-583 follow-up ticket).
      actionType: 'send_educational_message',
      ctaVerb: 'Present',
      cited_sources: [
        {
          id: 'CDS-Adoption-Lit-2024',
          title:
            'Pre-pilot CDS adoption literature benchmarks (illustrative projection)',
          year: 2024,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Shown in 71% of Metabolic Syndrome Pathway Step 1 visits',
    },
  ],
  ethnic_subgroup: 'south_asian',
  soapDraft: {
    cc: 'T2DM follow-up. Discuss MetSyn risk stratification and Metabolic Syndrome Pathway enrollment.',
    hpi: '48M with type 2 diabetes (HbA1c 7.4 on metformin 1000 mg twice daily), hypertension on amlodipine, and mixed hyperlipidemia on rosuvastatin 10 mg. Recent CAC scoring zero, but ApoB elevated at 112 despite LDL 88. Lp(a) 38. hsCRP 4.2. Fatigue and post-prandial somnolence. No chest pain or claudication.',
    assessment:
      'Metabolic syndrome (4 of 5 IDF criteria: waist, TG, HDL, BP) with discordant CAC and ApoB profile. Statin under-treated by ApoB despite LDL at target; particle burden remains elevated. Metabolic Syndrome Pathway candidate.',
    plan: 'Order Metabolic Syndrome Pathway fasting metabolic panel. Dispatch DQOLY plus AUDIT-C PROMs. Schedule Metabolic Syndrome Pathway Step 2 visit at week 2 for risk stratification and statin intensification decision (rosuvastatin 20 mg consideration). Counsel on Mediterranean diet and structured activity.',
  },
};
